'use client';

import {
  Pagination,
  PaginationList,
  PaginationNext,
  PaginationPage,
  PaginationPrevious
} from '@/components/catalyst/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/shadcn/select';
import { useRouter } from 'next/navigation';

export default function PulsePagination({
  page,
  totalPages,
  pageSize
}: {
  page: number;
  totalPages: number;
  pageSize: number;
}) {
  const router = useRouter();

  const handlePageChange = (newPage: number) => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('page', newPage.toString());
    const prevScrollY = window.scrollY;

    router.replace(currentUrl.pathname + currentUrl.search, { scroll: false });

    // For fresh page loads, manually restore scroll position after a delay, find better solution
    setTimeout(() => {
      window.scrollTo(0, prevScrollY);
    }, 200);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxPagesShown = 7;
    const pagesBetweenEllipsis = 5;

    if (totalPages <= maxPagesShown - 1) {
      // Show all pages if total is small enough
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const pagesBetweenCurAndEllipsis = Math.floor(pagesBetweenEllipsis / 2);
      const addBeginningEllipsis =
        maxPagesShown - pagesBetweenCurAndEllipsis - 1;
      const addEndEllipsis = totalPages - pagesBetweenCurAndEllipsis - 1;
      if (page <= addBeginningEllipsis) {
        // Near the beginning (last after ellipsis)
        for (let i = 1; i <= maxPagesShown - 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (page >= addEndEllipsis) {
        // Near the end (first before ellipsis)
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - maxPagesShown + 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // In the middle (between both ellipsis)
        pages.push(1);
        pages.push('...');
        for (
          let i = page - pagesBetweenCurAndEllipsis;
          i <= page + pagesBetweenCurAndEllipsis;
          i++
        ) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="flex justify-end">
      <Pagination>
        <PaginationPrevious
          onClick={() => page > 1 && handlePageChange(page - 1)}
        />
        <PaginationList>
          {renderPageNumbers().map((pageNum, index) =>
            pageNum === '...' ? (
              <span
                key={`ellipsis-${index}-${pageNum}`}
                className="px-2 py-1 text-muted-foreground"
              >
                ...
              </span>
            ) : (
              <PaginationPage
                key={`page-${pageNum}-${index}`}
                onClick={() => handlePageChange(pageNum as number)}
                current={pageNum === page}
              >
                {pageNum}
              </PaginationPage>
            )
          )}
        </PaginationList>
        <PaginationNext
          onClick={() => page < totalPages && handlePageChange(page + 1)}
        />
        <PageSizeSelector pageSize={pageSize} />
      </Pagination>
    </div>
  );
}

function PageSizeSelector({ pageSize }: { pageSize: number }) {
  const router = useRouter();

  const pageSizeOptions = [
    { value: '10', title: '10 / page' },
    { value: '25', title: '25 / page' },
    { value: '50', title: '50 / page' },
    { value: '100', title: '100 / page' }
  ];

  const handlePageSizeChange = (value: string) => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('pageSize', value);
    currentUrl.searchParams.set('page', '1');
    router.push(currentUrl.pathname + currentUrl.search);
  };

  return (
    <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
      <SelectTrigger className="h-8 w-fit text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {pageSizeOptions.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.title}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
